"use client";

import { cn } from "@jesus-central-church/shared";
import { AnimatedImage, Image as UiImage } from "@jesus-central-church/ui";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { SbImage } from "@storyblok/types/287435740670216/storyblok-components";
import { StoryblokAsset } from "@storyblok/types/storyblok";

interface ImageProperties {
  blok: SbImage & SbBlokData;

  className?: string;
  imageClassName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const optimizeImageStoryblok = (image: StoryblokAsset): string => {
  let imageSource = image.filename + `/m/1920x0`;
  if (image.focus) imageSource += `/filters:focal(${image.focus})`;

  return imageSource!;
};

export function ImageComponent({
  blok,
  className,
  imageClassName,
}: ImageProperties) {
  if (!blok.image?.filename) {
    return null;
  }

  const loadingStrategy = blok.priority
    ? undefined
    : // eslint-disable-next-line unicorn/no-nested-ternary
      blok.loading === ""
      ? "lazy"
      : (blok.loading as "lazy" | "eager" | undefined);

  const optimizedSrc = optimizeImageStoryblok(blok.image);

  if (blok.animated) {
    return (
      <div {...storyblokEditable(blok as SbBlokData)}>
        <AnimatedImage
          src={optimizedSrc}
          alt={blok.image.alt || ""}
          side={blok.side as "left" | "right"}
          delay={blok.delay ? Number.parseFloat(blok.delay) : 0}
          className={className}
          imageClassName={imageClassName}
          priority={blok.priority}
          loading={loadingStrategy as "lazy" | "eager" | undefined}
        />
      </div>
    );
  }

  const objectFit = (blok.objectFit || "cover") as
    | "cover"
    | "contain"
    | "fill"
    | "none"
    | "scale-down";

  return (
    <div
      {...storyblokEditable(blok as SbBlokData)}
      className={cn("relative aspect-[4/3] w-full", className)}
    >
      <UiImage
        src={optimizedSrc}
        alt={blok.image.alt || ""}
        fill
        objectFit={objectFit}
        priority={blok.priority}
        loading={blok.loading as "lazy" | "eager" | undefined}
        className={cn("overflow-hidden rounded-lg shadow-lg", imageClassName)}
      />
    </div>
  );
}
